import React, { useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';
import { updateOpenGraphMetaTags, resetOpenGraphMetaTags } from '../../utils/metaTags';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const LogoContainer = styled.div`
  margin: 0 0 16px 0;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px; /* Ensure container has minimum height */
  width: 100%;
`;

const Logo = styled.img`
  max-width: 60%;
  max-height: 200px; /* Set maximum height to prevent oversized logos */
  width: auto;
  height: auto;
  
  /* Scale down more gradually as screen gets smaller */
  @media (max-width: ${theme.breakpoints.desktop}) {
    max-width: 65%;
    max-height: 180px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 70%;
    max-height: 160px;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    max-width: 75%;
    max-height: 140px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 80%;
    max-height: 120px;
  }
`;

const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 16px;
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 40px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  /* Position relative to ensure proper stacking context */
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
    margin-bottom: 60px; /* Extra bottom spacing on mobile for better visibility */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
  
  /* Extra spacing for vertically short screens */
  @media (max-height: 600px) {
    margin-bottom: 80px; /* Extra bottom spacing for short screens */
  }
`;

const MediaAppearanceScreen = ({ data, onNext, userProgress = {} }) => {
  // Update Open Graph meta tags when component mounts or data changes
  useEffect(() => {
    const ogTags = data.content?.ogMetaTags;
    if (ogTags) {
      // Check if at least one tag has a non-empty value
      const hasValues = Object.values(ogTags).some(value => {
        if (value === null || value === undefined) return false;
        const stringValue = String(value).trim();
        return stringValue !== '';
      });
      
      if (hasValues) {
        // Only update if at least one tag has a value
        updateOpenGraphMetaTags(ogTags);
      }
    }

    // Cleanup: reset to defaults when component unmounts
    return () => {
      resetOpenGraphMetaTags();
    };
  }, [data.content?.ogMetaTags]);

  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Get logo image URL, checking multiple possible paths and trimming whitespace
  const logoImageUrl = (data.content?.logoImage || data.logoImage)?.trim();
  
  // Debug: Log if logoImage is expected but missing (only in development)
  if (process.env.NODE_ENV === 'development' && !logoImageUrl && data.content) {
    console.log('MediaAppearanceScreen: No logoImage found. Available content keys:', Object.keys(data.content));
  }

  return (
    <Container>
      <Content>
        {/* Logo Image - centered at the top */}
        {logoImageUrl && (
          <LogoContainer>
            <Logo 
              src={logoImageUrl} 
              alt={data.content?.logoAlt || 'Media Appearance'}
              onError={(e) => {
                console.error('Failed to load logo image:', logoImageUrl);
                // Hide the image on error, but keep the container
                e.target.style.display = 'none';
              }}
            />
          </LogoContainer>
        )}
        
        {/* Screen description text below the logo */}
        {htmlContent && (
          <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default MediaAppearanceScreen;
